import { ProductCategory, ScreenName, SectionName } from "@utils/constants";
import React, { useEffect, useState, useRef } from "react";
import { useGetScreenData } from "../../../hooks/useGetScreenData";
import { Linking, Platform, TouchableOpacity, View } from "react-native";
import TabIcon from "@navigations/utils/TabIcon";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import {
  CommonActions,
  getFocusedRouteNameFromRoute,
  useLinkTo,
  useNavigation,
  useNavigationState,
  useRoute,
} from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import styled from "styled-components/native";
import { Divider } from "@gluestack-ui/themed";
import Routes from "@navigations/Routes";
import {
  navigate as globalNavigate,
  removeOrderNowScreenKeepingFocus,
} from "@navigations/utils/RootNavigation";
import { OrderNowLeaveGuard } from "@scenes/searchResults/orderNowLeaveGuard";
import useProductArray from "../hooks/useProductArray";
import useCollectionNavigation from "../hooks/useCollectionNavigation";
import { deepCompareObjects } from "./utils";
import {
  resetAuxPayload,
  resetFiltersPayload,
  setFiltersPayload,
  setPayloadData,
  setSectionName,
  useSearchFiltersSlice,
} from "@redux/slices/searchFilters";
import { useDispatch, useSelector } from "react-redux";
import { SpaceType } from "@components/atoms/Spacer/types";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { useUnreadRequestCountData } from "@apis/requestHistory";
import BadgeDot from "@components/atoms/BadgeDot";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { setLoginModalType, setProductType } from "@redux/slices/appStateSlice";

import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { VDBText } from "@components/atoms";
import { VDBIcons } from "@utils/VDBIcons";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { findDefaultScreenTab } from "@navigations/utils/convertTabsData";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { getActiveRouteName } from "@utils/analytics.web";
import {
  getBoxShadow,
  replaceDeeplinkScheme,
} from "@utils/product/productValidators";
import urlEventEmitter, { URL_EVENTS } from "@utils/custom-url-event-emitter";
import { DefaultTheme } from "styled-components/native";
import {
  BreadCrumbLabel,
  NavigationHistoryState,
} from "@components/molecules/Breadcrumbs/utils";
import {
  BuilderAlertManager,
  shouldConfirmHeaderNavigation,
} from "@scenes/jewelry-builder/components/BuilderAlertManager";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";

const NavigationBarWeb = () => {
  const navigation = useNavigation();
  const tabsData = useGetScreenData(ScreenName.TABS);
  const { clearRouteHistory, addRouteToHistory } = useNavigationHistory();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const { theme } = useScreenOptionsHooks();
  const { t } = useTranslation();
  const route = useRoute();
  const typography = useTypographyStore();
  const userInfo = useUserInfo();
  const hasBadge = userInfo.user.price_mode !== PriceModes.GREEN;
  const dispatch = useDispatch();
  const activeProduct = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentRouteName = [Routes.PDPScreen].includes(
    getFocusedRouteNameFromRoute(route) as Routes
  )
    ? Routes.ExploreNavigator
    : getFocusedRouteNameFromRoute(route) ?? "";
  const { skipFilterScreen } = useProductArray();
  const { selectCollectionOption } = useCollectionNavigation();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoverTab, setHoverTab] = useState<number | null>(null);
  const linkTo = useLinkTo();
  const activeRouteName = useNavigationState((state) =>
    getActiveRouteName(state)
  );

  const focusedProductType = useNavigationState((state) => {
    const getLeafRoute = (s: any): any => {
      if (!s?.routes?.length) return undefined;
      const idx = typeof s.index === "number" ? s.index : s.routes.length - 1;
      const currentRouteEntry = s.routes[idx];
      if (currentRouteEntry?.state) {
        return getLeafRoute(currentRouteEntry.state);
      }
      return currentRouteEntry;
    };
    const leaf = getLeafRoute(state);
    return (
      leaf?.params?.productType ??
      leaf?.params?.params?.productType ??
      undefined
    );
  });
  const { history } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );

  const currentRoute = useNavigationState((state) => {
    if (state?.routes.length === 1 && state.routes[0].name === "main") {
      return state.routes[0]?.state?.routes?.find(
        (r) => r.name === currentRouteName
      );
    }
    return state?.routes.find((r) => r.name === currentRouteName);
  });
  const buyerData = useUnreadRequestCountData("buyer", "all", true, hasBadge);
  const sellerData = useUnreadRequestCountData("seller", "all", true, hasBadge);
  const { sectionName: filterSectionName } = useSearchFiltersSlice();

  const favoritesTabRef = useRef(null);
  const lastHomeRef = useRef<{ screen: string; params?: any } | null>(null);
  const lastProductStateRef = useRef<{
    productType?: string;
    sectionName?: string;
    targetJewelryType?: string;
    topScreen?: string;
  } | null>(null);

  const shouldRestorePreviousProduct = (
    productType?: string,
    sectionName?: string
  ) => {
    const last = lastProductStateRef.current;
    if (currentRouteName === Routes.ExploreNavigator || !last) return false;
    if (last.productType !== productType) return false;
    if (
      !last.topScreen ||
      last.topScreen === Routes.Explore ||
      last.topScreen === Routes.ReviewPage
    ) {
      return false;
    }
    if (sectionName && last.sectionName && last.sectionName !== sectionName) {
      return false;
    }
    return true;
  };

  const resetExploreToScreen = (targetScreen: string, params?: object) => {
    navigation.dispatch((state) => {
      const mainRoute = state.routes.find((r: any) => r.name === "main");
      const tabState = mainRoute?.state;
      const hasExploreNavigator = tabState?.routes?.some(
        (r: any) => r.name === Routes.ExploreNavigator
      );

      if (!tabState?.routes || !hasExploreNavigator) {
        return CommonActions.navigate({
          name: Routes.AppNavigator,
          params: {
            screen: Routes.ExploreNavigator,
            params: {
              screen: targetScreen,
              params,
            },
          },
        });
      }

      const updatedTabRoutes = tabState.routes.map((mRoute: any) => {
        if (mRoute.name === Routes.ExploreNavigator) {
          return {
            ...mRoute,
            state: {
              routes: [
                {
                  name: targetScreen,
                  params,
                },
              ],
              index: 0,
            },
          };
        }
        return mRoute;
      });
      const exploreIndex = updatedTabRoutes.findIndex(
        (r: any) => r.name === Routes.ExploreNavigator
      );

      return CommonActions.reset({
        ...state,
        index: 0,
        routes: [
          {
            ...mainRoute,
            state: {
              ...tabState,
              routes: updatedTabRoutes,
              index: exploreIndex >= 0 ? exploreIndex : tabState.index,
            },
          },
        ],
      });
    });
  };

  const showBadge = (tabRoute: string) => {
    if (hasBadge && tabRoute === Routes.RequestHistoryNavigator) {
      return (
        // @ts-ignore
        (sellerData?.data?.data?.count !== 0 &&
          // @ts-ignore
          sellerData?.data?.data?.count !== undefined) ||
        // @ts-ignore
        (buyerData?.data?.data?.count !== 0 &&
          // @ts-ignore
          buyerData?.data?.data?.count !== undefined)
      );
    }
    return false;
  };

  useEffect(() => {
    if (currentRouteName === Routes.HomeNavigator) {
      const nestedState = (currentRoute as any)?.state;
      const nested = nestedState?.routes?.[nestedState?.index] ?? null;
      if (nested?.name) {
        lastHomeRef.current = { screen: nested.name, params: nested.params };
      } else {
        lastHomeRef.current = { screen: Routes.HomeScreen, params: {} };
      }
    }

    // Track product state when on ExploreNavigator
    if (currentRouteName === Routes.ExploreNavigator && activeProduct) {
      const nestedState = (currentRoute as any)?.state;
      const nested = nestedState?.routes?.[nestedState?.index] ?? null;
      lastProductStateRef.current = {
        productType: activeProduct,
        sectionName: nested?.params?.sectionName,
        targetJewelryType: nested?.params?.targetJewelryType,
        topScreen: nested?.name,
      };
    }

    let index = currentIndex;
    const filteredComponent = tabsData.filter(
      (tab: any) => tab.name === currentRouteName
    );
    const currentProductType =
      focusedProductType ||
      // @ts-ignore
      currentRoute?.state?.routes[currentRoute?.state?.index]?.params
        ?.productType ||
      activeProduct;
    const isSectionNameMatch = (tab: any) =>
      tab.sectionName === filterSectionName;
    const isRouteNameMatch = (tab: any) => tab.name === currentRouteName;
    const isProductTypeMatch = (tab: any) =>
      tab?.params?.productType === currentProductType;

    if (filteredComponent.length > 1) {
      index = tabsData.findIndex((tab: any) =>
        deepCompareObjects(
          tab?.params,
          currentRoute?.state?.routes[currentRoute?.state?.index ?? 0]
            ?.params ||
            // @ts-ignore
            currentRoute?.params?.params ||
            {}
        )
      );
    } else {
      if (currentRouteName) {
        index = tabsData.findIndex((tab: any) => tab.name === currentRouteName);
      } else {
        index = tabsData.findIndex(
          (tab: any) => tab.name === findDefaultScreenTab(tabsData)
        );
      }
    }
    if (
      currentRoute?.state?.routes[currentRoute?.state?.index ?? 0]?.name ===
      Routes.SavedSearch
    ) {
      index = tabsData.findIndex((tab: any) => {
        return (
          tab.name ===
          currentRoute?.state?.routes[currentRoute?.state?.index ?? 0]?.name
        );
      });
    } else if (
      index === -1 ||
      [
        SectionName.JEWELRY,
        SectionName.LABGROWN_JEWELRY,
        SectionName.SINGLE_STONES,
      ].includes(filterSectionName)
    ) {
      let productIndex = tabsData.findIndex((tab: any) => {
        if (
          activeRouteName === Routes.GlobalNavigator ||
          route.name === Routes.GlobalNavigator
        ) {
          return isSectionNameMatch(tab) && isProductTypeMatch(tab);
        } else {
          return (
            isSectionNameMatch(tab) &&
            isRouteNameMatch(tab) &&
            isProductTypeMatch(tab)
          );
        }
      });
      if (productIndex !== -1) index = productIndex;
      else
        productIndex = tabsData.findIndex(
          (tab: any) => isRouteNameMatch(tab) && isProductTypeMatch(tab)
        );

      if (productIndex !== -1) {
        index = productIndex;
      }
    }
    if (
      index < 0 &&
      !(
        activeRouteName === Routes.BulkRequestNavigator ||
        activeRouteName === Routes.OrderCartScreen
      )
    )
      index = 0;

    if (activeRouteName === Routes.Explore) {
      index = 0;
    }

    if (activeRouteName === Routes.AccountOverviewScreen) {
      index = -1;
    }
    setCurrentIndex(index);
  }, [
    currentRouteName,
    currentRoute,
    route.name,
    activeProduct,
    focusedProductType,
  ]);

  const navigateToComponent = (tabData: any) => {
    setHoveredIndex(null);
    setHoverTab(null);
    if (tabData.name === Routes.ExploreNavigator) {
      tabData = {
        ...tabData,
        params: {
          ...tabData.params,
          productType: tabData?.productType ?? tabData?.params?.productType,
          sectionName: tabData?.sectionName ?? tabData?.params?.sectionName,
          productPrefix:
            tabData?.productPrefix ??
            tabData?.params?.productPrefix ??
            tabData?.params?.productType ??
            tabData?.productType,
        },
      };

      if (
        tabData.isProduct &&
        (tabData?.productType === ProductCategory.JEWELRY ||
          tabData?.productType === ProductCategory.LABGROWN_JEWELRY)
      ) {
        const mRoute = {
          name: Routes.FilterSplitScreen,
          params: {
            ...tabData.params,
            breadCrumbLabel: BreadCrumbLabel.SETTING_SEARCH,
          },
        };
        if (enableJewelryBuilder) {
          addRouteToHistory(mRoute);
        }
      }
    }
    if (tabData.name === Routes.ExploreNavigator && tabData.isProduct) {
      const productType = tabData?.productType;
      const isReturningFromDifferentTab = shouldRestorePreviousProduct(
        productType,
        tabData?.sectionName ?? tabData?.params?.sectionName
      );

      if (isReturningFromDifferentTab) {
        if (!OrderNowLeaveGuard.isActive()) {
          removeOrderNowScreenKeepingFocus();
        }
        dispatch(setProductType(productType));
        globalNavigate(Routes.ExploreNavigator);
      } else {
        dispatch(setProductType(productType));
        dispatchFilterPayload({
          productType: productType,
          searchPayload: tabData?.search_payload,
          sectionName: tabData?.sectionName,
          payloadName: tabData?.payloadName,
        });
        const skipFilterScreenForWeb = theme.isLargeScreen
          ? skipFilterScreen(productType)
          : false;
        const targetScreen = skipFilterScreenForWeb
          ? Routes.FilterSplitScreen
          : Routes.FilterScreen;
        resetExploreToScreen(targetScreen, tabData?.params);
      }
    } else if (tabData.name === Routes.ExploreNavigator) {
      dispatch(setSectionName(""));
      dispatch(setProductType(""));
      // Use global navigate to bypass nested navigator restrictions
      globalNavigate(Routes.ExploreNavigator, {
        screen: Routes.Explore,
        initial: true,
      });
    } else if (tabData.name === Routes.HomeNavigator) {
      const isAlreadyInHome = currentRouteName === Routes.HomeNavigator;
      if (isAlreadyInHome) {
        globalNavigate(Routes.HomeNavigator, {
          screen: Routes.HomeScreen,
          params: { resetToDefault: true },
          initial: false,
        });
      } else {
        const targetParams = lastHomeRef.current?.params ?? {};
        globalNavigate(Routes.AppNavigator, {
          screen: Routes.HomeNavigator,
          params: targetParams,
        });
      }
    } else if (tabData.screen_name) {
      // Use global navigate for nested screen navigation
      globalNavigate(tabData.name, {
        screen: tabData.screen_name,
        params: tabData?.params ?? {},
      });
    } else {
      const tabNavigatorScreens = [
        Routes.FavoritesNavigator,
        Routes.HomeNavigator,
        Routes.PagesNavigator,
        Routes.MoreNavigator,
        Routes.RequestHistoryNavigator,
        Routes.SavedSearch,
        Routes.PagesNavigator,
      ];

      if (tabNavigatorScreens.includes(tabData.name)) {
        globalNavigate(Routes.AppNavigator, {
          screen: tabData.name,
          params: tabData?.params ?? {},
        });
      } else {
        globalNavigate(tabData.name, tabData?.params ?? {});
      }
    }
  };

  const navigationSubOptionPress = ({
    tab,
    isProduct,
    productType,
    sectionName,
    payloadName,
    productPrefix,
  }: any) => {
    setHoveredIndex(null);
    setHoverTab(null);
    if (tab?.deeplink) {
      let url = replaceDeeplinkScheme(
        tab?.deeplink,
        process.env.EXPO_PUBLIC_APP_SCHEME
      );
      if (Platform.OS === "web") {
        linkTo(url);
      } else {
        Linking.openURL(url);
      }
    } else if (
      tab?.is_collection ||
      tab?.collection ||
      tab?.predefined_payload
    ) {
      selectCollectionOption(tab, {
        productType,
        sectionName: tab?.sectionName ?? sectionName,
        productPrefix,
      });
    } else if (isProduct)
      productSubOptionPress({
        selectedType: tab,
        productType,
        sectionName: tab?.sectionName ?? sectionName,
        payloadName,
        productPrefix,
      });
  };

  const productSubOptionPress = ({
    productType,
    selectedType,
    sectionName,
    payloadName,
    productPrefix,
  }: any) => {
    if (selectedType && sectionName && payloadName) {
      const isReturningFromDifferentTab =
        shouldRestorePreviousProduct(productType, sectionName) &&
        lastProductStateRef.current?.targetJewelryType === selectedType.name;

      if (isReturningFromDifferentTab) {
        if (!OrderNowLeaveGuard.isActive()) {
          removeOrderNowScreenKeepingFocus();
        }
        dispatch(setProductType(productType));
        globalNavigate(Routes.ExploreNavigator);
      } else {
        dispatchFilterPayload({
          productType,
          searchPayload: selectedType.search_payload,
          sectionName,
          payloadName,
        });

        const isJewelryType =
          productType === ProductCategory.JEWELRY ||
          productType === ProductCategory.LABGROWN_JEWELRY ||
          sectionName === SectionName.JEWELRY ||
          sectionName === SectionName.LABGROWN_JEWELRY;

        const skipFilterScreenForWeb = theme.isLargeScreen
          ? skipFilterScreen(productType)
          : false;
        setTimeout(() => {
          const params = {
            breadCrumbLabel: BreadCrumbLabel.SETTING_SEARCH,
            productType: productType,
            sectionName,
            filterSplitStep: 2,
            productPrefix,
            targetJewelryType: isJewelryType ? selectedType.name : null,
          };
          const name = enableJewelryBuilder
            ? Routes.FilterSplitScreen
            : skipFilterScreenForWeb
            ? Routes.FilterSplitScreen
            : Routes.FilterScreen;
          const xRoute = { name, params };
          if (enableJewelryBuilder) {
            addRouteToHistory(xRoute);
          }

          if (!OrderNowLeaveGuard.isActive()) {
            removeOrderNowScreenKeepingFocus();
          }
          globalNavigate(Routes.ExploreNavigator, {
            screen: name,
            params: params,
          });

          if (isJewelryType && selectedType.name) {
            setTimeout(() => {
              urlEventEmitter.emit(URL_EVENTS.FOCUS_JEWELRY_TYPE, {
                jewelryType: selectedType.name,
                sectionName,
                searchPayload: selectedType.search_payload,
              });
            }, 300);
          }
        }, 100);
      }
    }
  };

  const dispatchFilterPayload = ({
    productType,
    searchPayload,
    sectionName,
    payloadName,
  }: any) => {
    dispatch(resetAuxPayload());
    const payload: any = {};

    if (payloadName && Array.isArray(payloadName)) {
      payloadName.forEach((name: string) => {
        if (searchPayload) {
          payload[name] = Array.isArray(searchPayload)
            ? searchPayload.flat()
            : typeof searchPayload === "string"
            ? searchPayload
            : "";
        }
      });
    }

    dispatch(setProductType(productType));
    dispatch(setSectionName(sectionName));

    if (Object.keys(payload).length > 0) {
      dispatch(
        setFiltersPayload({
          sectionName: sectionName,
          data: payload,
        })
      );

      dispatch(
        setPayloadData({
          productType: productType,
          data: { [sectionName]: payload },
        })
      );
    }
  };

  const NavigationSubOptions = ({
    options,
    isProduct,
    productType,
    sectionName,
    payloadName,
    productPrefix,
    hoverIndex,
  }: any) => {
    return (
      <SubOptionsContainer onHoverIn={() => setHoveredIndex(hoverIndex)}>
        {options && options.length
          ? options.map((tab: any, i: number) => {
              return (
                <NavigationSubOptionTab
                  key={i}
                  index={i}
                  totalOptions={options.length}
                  tab={tab}
                  isProduct={isProduct}
                  productType={productType}
                  sectionName={sectionName}
                  payloadName={payloadName}
                  productPrefix={productPrefix}
                />
              );
            })
          : null}
      </SubOptionsContainer>
    );
  };

  const NavigationSubOptionTab = ({
    tab,
    isProduct,
    productType,
    sectionName,
    payloadName,
    productPrefix,
    totalOptions,
    index,
  }: any) => {
    return (
      <>
        <TouchableOpacity
          onPress={() => {
            const callback = () => {
              navigationSubOptionPress({
                tab,
                isProduct,
                productType,
                sectionName,
                payloadName,
                productPrefix,
              });
            };

            if (
              shouldConfirmHeaderNavigation(
                enableJewelryBuilder,
                history.length
              )
            ) {
              BuilderAlertManager.open(callback);
            } else {
              if (enableJewelryBuilder) {
                dispatch(resetFiltersPayload());
                if (history.length > 0) {
                  // @ts-ignore
                  navigation.pop(history.length);
                }
                clearRouteHistory();
              }
              callback();
            }
          }}
        >
          <Overlay index={index} optionsLength={totalOptions}>
            <LeftLabelContainer>
              <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
                {t(tab.label)}
              </VDBText>
            </LeftLabelContainer>
            <RightLabelContainer>
              <VDBIcon
                name={VDBIcons.ARROW_RIGHT}
                size={24}
                color={theme["core-04"]}
                variant={IconsVariant.VDBCustom}
              />
            </RightLabelContainer>
          </Overlay>
        </TouchableOpacity>
        {index !== totalOptions - 1 && (
          <LineViewContainer background="core-03" marginLeft={true} />
        )}
      </>
    );
  };

  return (
    <Container>
      {Object.values(
        tabsData.reduce((acc: any, tab: any, index: number) => {
          const key = tab.isProduct ? "product" : tab.componentName;
          if (!acc[key]) {
            acc[key] = { items: [], startIndex: index };
          }
          acc[key].items.push(tab);
          return acc;
        }, {} as Record<string, { items: any[]; startIndex: number }>)
      ).map((group: any, groupIndex: number) => {
        const { items, startIndex } = group;
        const activeIndex =
          currentIndex >= startIndex && currentIndex < startIndex + items.length
            ? currentIndex - startIndex
            : -1;

        if (items.length && items.some((tab: any) => tab.isProduct) === true) {
          return (
            <FlexContainer id="products" key={`group-${groupIndex}`}>
              {items.map((tab: any, i: number) => {
                const isActive = i === activeIndex || i === hoveredIndex;
                const style = {
                  width: isActive ? 24 : 0,
                  transform: [{ scale: isActive ? 1 : 0 }],
                  transition: "all 200ms ease-in-out",
                };

                const isFavoritesTab = tab.name === Routes.FavoritesNavigator;
                const tabRef = isFavoritesTab ? favoritesTabRef : null;
                return (
                  <InnerContainer
                    key={`${tab.name}-${i}` || `tab-${i}`}
                    focused={i === activeIndex}
                    onPress={() => {
                      if (userInfo.isGuest && tab.isRestricted === true) {
                        dispatch(setLoginModalType("default"));
                      } else {
                        const callback = () => {
                          navigateToComponent(tab);
                        };
                        if (
                          shouldConfirmHeaderNavigation(
                            enableJewelryBuilder,
                            history.length
                          )
                        ) {
                          BuilderAlertManager.open(callback);
                        } else {
                          if (enableJewelryBuilder) {
                            if (history.length > 0) {
                              // @ts-ignore
                              navigation.pop(history.length);
                            }
                            clearRouteHistory();
                          }
                          callback();
                        }
                      }
                    }}
                    onHoverIn={() => setHoveredIndex(i)}
                    onHoverOut={() => setHoveredIndex(null)}
                    ref={tabRef}
                    {...(isFavoritesTab &&
                      Platform.OS === "web" && {
                        id: "favorites-tab",
                        nativeID: "favorites-tab",
                        testID: "favorites-tab",
                        accessibilityLabel: "Favorites tab",
                      })}
                  >
                    <TabContainer
                      showBadge={showBadge(tab.name) && !userInfo.isPAUser()}
                    >
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <Animated.View style={style}>
                          {(i === activeIndex || i === hoveredIndex) && (
                            <TabIcon
                              icon={tab.icon?.data}
                              mainIconType={tab.icon?.type}
                              color={
                                theme[
                                  tab.focusedColorCode as keyof DefaultTheme
                                ] as string
                              }
                              route={route}
                            />
                          )}
                        </Animated.View>
                        <View>
                          <VDBText
                            type={
                              i === hoveredIndex || i === activeIndex
                                ? typography["NAV-M"]
                                : typography["NAV-R"]
                            }
                            color={
                              i === hoveredIndex || i === activeIndex
                                ? (theme[
                                    tab.focusedColorCode as keyof DefaultTheme
                                  ] as string)
                                : theme["core-05"]
                            }
                            isUpperCase
                          >
                            {t(tab.label)}
                          </VDBText>
                        </View>
                        {showBadge(tab.name) && !userInfo.isPAUser() && (
                          <BadgeDot isDesktop={theme.isLargeScreen} />
                        )}
                      </View>
                    </TabContainer>
                    <Divider
                      height={
                        [activeIndex, activeIndex - 1].includes(i) ? 56 : 20
                      }
                      orientation="vertical"
                      position="absolute"
                      right={0}
                    />
                    {i === hoveredIndex &&
                    tab.sub_section &&
                    tab.sub_section.length ? (
                      <NavigationSubOptions
                        options={[...tab.sub_section].sort(
                          (a, b) => a.order - b.order
                        )}
                        isProduct={true}
                        productType={tab.productType}
                        sectionName={tab.sectionName}
                        payloadName={[tab.payloadName]}
                        productPrefix={tab.productPrefix ?? tab.productType}
                        hoverIndex={i}
                      />
                    ) : null}
                  </InnerContainer>
                );
              })}
            </FlexContainer>
          );
        }

        const tab = items[0];
        const isFocused = startIndex === currentIndex;
        const isHovered = groupIndex === hoverTab;
        const shouldExpand = isFocused || isHovered;
        const iconSize = useSharedValue(0);
        const scale = useSharedValue(shouldExpand ? 0 : 1);
        const tabIconStyle = useAnimatedStyle(() => ({
          transform: [{ scale: scale.value }],
          width: iconSize.value,
        }));

        React.useEffect(() => {
          iconSize.value = withTiming(shouldExpand ? 24 : 0, {
            duration: 200,
            easing: Easing.in(Easing.cubic),
          });
          scale.value = withTiming(shouldExpand ? 1 : 0, {
            duration: 200,
            easing: Easing.out(Easing.cubic),
          });
        }, [isHovered, isFocused]);

        const isFavoritesTab = tab.name === Routes.FavoritesNavigator;
        const tabRef = isFavoritesTab ? favoritesTabRef : null;

        return (
          <FlexContainer
            id={tab.isProduct ? "products" : tab.name}
            key={`single-${groupIndex}`}
          >
            <InnerContainer
              focused={isFocused}
              onPress={() => {
                if (userInfo.isGuest && tab.isRestricted === true) {
                  dispatch(setLoginModalType("default"));
                } else {
                  const callback = () => {
                    navigateToComponent(tab);
                  };
                  if (
                    shouldConfirmHeaderNavigation(
                      enableJewelryBuilder,
                      history.length
                    )
                  ) {
                    BuilderAlertManager.open(callback);
                  } else {
                    if (enableJewelryBuilder) {
                      if (history.length > 0) {
                        // @ts-ignore
                        navigation.pop(history.length);
                      }
                      clearRouteHistory();
                    }
                    callback();
                  }
                }
              }}
              onHoverIn={() => setHoverTab(groupIndex)}
              onHoverOut={() => setHoverTab(null)}
              ref={tabRef}
              {...(isFavoritesTab &&
                Platform.OS === "web" && {
                  id: "favorites-tab",
                  nativeID: "favorites-tab",
                  testID: "favorites-tab",
                  accessibilityLabel: "Favorites tab",
                })}
            >
              <TabContainer
                showBadge={showBadge(tab.name) && !userInfo.isPAUser()}
              >
                <View
                  style={[
                    {
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 5,
                    },
                  ]}
                  id={tab.name}
                >
                  <Animated.View style={[tabIconStyle]}>
                    {shouldExpand && (
                      <TabIcon
                        icon={tab.icon?.data}
                        mainIconType={tab.icon?.type}
                        color={
                          theme[
                            tab.focusedColorCode as keyof DefaultTheme
                          ] as string
                        }
                        route={route}
                        size={iconSize.value}
                      />
                    )}
                  </Animated.View>
                  <View
                    style={{
                      color: shouldExpand
                        ? (theme[
                            tab.focusedColorCode as keyof DefaultTheme
                          ] as string)
                        : theme["core-05"],
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      height: 30,
                    }}
                  >
                    <VDBText
                      type={
                        shouldExpand ? typography["NAV-M"] : typography["NAV-R"]
                      }
                      color={
                        shouldExpand
                          ? (theme[
                              tab.focusedColorCode as keyof DefaultTheme
                            ] as string)
                          : theme["core-05"]
                      }
                      isUpperCase
                    >
                      {t(tab.label)}
                    </VDBText>
                  </View>
                </View>
                {showBadge(tab.name) && !userInfo.isPAUser() && (
                  <BadgeDot isDesktop={theme.isLargeScreen} />
                )}
              </TabContainer>
            </InnerContainer>
            <Divider
              height={
                [currentIndex, currentIndex - 1].includes(startIndex) ? 56 : 20
              }
              orientation="vertical"
              position="absolute"
              right={0}
            />
          </FlexContainer>
        );
      })}
    </Container>
  );
};

const Container = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  border: ${({ theme }: { theme: DefaultTheme }) =>
    `1px solid ${theme["core-03"]}`};
  borderleftwidth: 0;
  borderrightwidth: 0;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    `${theme["core-01"]}`};
  height: 56px;
  z-index: 1;
`;

const TabContainer = styled.View<{
  showBadge: boolean;
}>`
  padding-right: ${({ showBadge }: { showBadge: boolean }) =>
    `${showBadge ? "10px" : "0"}`};
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const InnerContainer = styled.Pressable<{ focused: boolean }>`
  padding: 0;
  margin: 0;
  justify-content: center;
  padding-left: 10px;
  padding-right: 10px;
  height: 53.5px;
  width: auto;
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.Small}px;
  background-color: ${({
    theme,
    focused,
  }: {
    theme: DefaultTheme;
    focused: boolean;
  }) => `${focused ? theme["core-02"] : theme["core-01"]}` as string};
`;

const FlexContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 56px;
`;

const SubOptionsContainer = styled.Pressable`
  position: absolute;
  z-index: 1;
  top: 55px;
  padding: 10px 0;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  width: max-content;
  left: 0;
  min-width: 300px;
  box-shadow: ${({ theme }: { theme: DefaultTheme }) =>
    getBoxShadow(theme.Shadows.Normal)};
  border-radius: 10px;
`;

const Overlay = styled.View<{ index: number; optionsLength: number }>`
  padding-top: ${({ index }: { index: number }) =>
    index === 0 ? "unset" : SpaceType.Extra};
  padding-bottom: ${({
    index,
    optionsLength,
  }: {
    index: number;
    optionsLength: number;
  }) => (index === optionsLength - 1 ? "unset" : SpaceType.Extra)};
  padding-left: ${SpaceType.Double};
  padding-right: ${SpaceType.Double};
  flex-direction: row;
  justify-content: space-between;
`;

const LeftLabelContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const RightLabelContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;
export default NavigationBarWeb;
